import { Component, inject, OnInit, EnvironmentInjector } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { APP_CONFIG } from './app.config';
import { RoutesHelper } from '@helpers/routes-helper';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AuthenticationService } from '@services/authentication';
import { CrossTabCommunicationService } from '@services/cross-tab/cross-tab-communication.service';
import { LoginResponse } from '@services/api-client/responses';
import { filter } from 'rxjs';
import { NgIf, AsyncPipe } from '@angular/common';
import { LayoutComponent } from './components/layout/layout.component';
import { ConnectLogicService } from '@services/ymodem/connect-logic.service';

@UntilDestroy()
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [NgIf, AsyncPipe, RouterOutlet, LayoutComponent]
})
export class AppComponent implements OnInit {
    private readonly router = inject(Router);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly crossTabService = inject(CrossTabCommunicationService);
    private readonly injector = inject(EnvironmentInjector);
    private readonly connectLogicService = inject(ConnectLogicService);

    public user$ = this.authenticationService.user$.pipe(untilDestroyed(this));
    public useLayout = true;

    private inactivityTimeout: ReturnType<typeof setTimeout> | null = null;
    private refreshTokenTimeout: ReturnType<typeof setTimeout> | null = null;
    private readonly onActivity = this.resetInactivityTimer.bind(this);
    private isCriticalOperationInProgress = false;

    private get inactivityLimit(): number {
        return APP_CONFIG.inactivityTimeoutMinutes * 60 * 1000;
    }

    public ngOnInit() {
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            //const navEndEvent = event as NavigationEnd;
            // this.useLayout =
            //     !navEndEvent.urlAfterRedirects.includes(APP_ROUTES.LicenseUpdate) &&
            //     !navEndEvent.urlAfterRedirects.includes(APP_ROUTES.LicenseConfirmation);
        });

        this.authenticationService.user$.pipe(untilDestroyed(this)).subscribe((user) => {
            if (user) {
                this.addInactivityListeners();
                this.resetInactivityTimer();
                this.scheduleTokenRefresh();
            } else {
                this.clearInactivityTimer();
                this.removeInactivityListeners();
                this.clearRefreshTimer();
            }
        });

        // Listen for logout from other tabs
        this.crossTabService.logout$.pipe(untilDestroyed(this)).subscribe(() => {
            // AuthenticationService already handled the logout cleanup
            // Just navigate to login page
            this.injector.runInContext(() => {
                RoutesHelper.RunInContext.navigateToLoginAndTriggerLogout();
            });
        });

        // Listen for token updates from other tabs and reschedule token refresh
        this.crossTabService.tokenUpdated$.pipe(untilDestroyed(this)).subscribe(() => {
            this.scheduleTokenRefresh();
        });

        // Monitor measurement upload and transfer state to prevent auto logout during critical operations
        this.connectLogicService.state$.pipe(untilDestroyed(this)).subscribe((state) => {
            const wasInProgress = this.isCriticalOperationInProgress;
            this.isCriticalOperationInProgress =
                state.isTransferring || state.uploadStatus.uploading > 0 || state.uploadStatus.pending > 0;

            // If operations just started, clear the existing timer to prevent logout during the operation
            if (!wasInProgress && this.isCriticalOperationInProgress && this.authenticationService.isAuthenticated()) {
                console.log('Critical operations started, clearing inactivity timer');
                this.clearInactivityTimer();
            }

            // If operations just completed, reset the timer to give user full timeout period
            if (wasInProgress && !this.isCriticalOperationInProgress && this.authenticationService.isAuthenticated()) {
                console.log('Critical operations completed, resetting inactivity timer');
                this.resetInactivityTimer();
            }
        });

        console.log(
            'time: ',
            new Date().toLocaleTimeString(),
            ' inactivityLimit (m):',
            this.inactivityLimit / 1000 / 60
        );
    }

    private addInactivityListeners() {
        window.addEventListener('mousemove', this.onActivity);
        window.addEventListener('keydown', this.onActivity);
        window.addEventListener('mousedown', this.onActivity);
        window.addEventListener('touchstart', this.onActivity);
    }

    private removeInactivityListeners() {
        window.removeEventListener('mousemove', this.onActivity);
        window.removeEventListener('keydown', this.onActivity);
        window.removeEventListener('mousedown', this.onActivity);
        window.removeEventListener('touchstart', this.onActivity);
    }

    private resetInactivityTimer() {
        this.clearInactivityTimer();

        // Don't start timeout if critical operations (file transfer or upload) are in progress
        if (this.isCriticalOperationInProgress) {
            console.log('Critical operation in progress (transfer or upload), skipping inactivity timeout');
            return;
        }

        this.inactivityTimeout = setTimeout(() => {
            // Double-check before logout - don't log out if critical operations are in progress
            if (this.isCriticalOperationInProgress) {
                console.log('Timer expired but critical operation in progress, not logging out');
                this.resetInactivityTimer(); // Reschedule
                return;
            }
            console.log('User inactive for too long, logging out');
            this.handleAutoLogout();
        }, this.inactivityLimit);
    }

    private clearInactivityTimer() {
        if (this.inactivityTimeout !== null) {
            clearTimeout(this.inactivityTimeout);
            this.inactivityTimeout = null;
        }
    }

    private clearRefreshTimer() {
        if (this.refreshTokenTimeout !== null) {
            clearTimeout(this.refreshTokenTimeout);
            this.refreshTokenTimeout = null;
        }
    }

    private getTokenExpiry(): number | null {
        const token = this.authenticationService.getToken();
        if (!token) return null;
        try {
            const payload = JSON.parse(atob(token.split('.')[1])) as { exp?: unknown };
            return typeof payload.exp === 'number' ? payload.exp : null;
        } catch {
            return null;
        }
    }

    private scheduleTokenRefresh() {
        this.clearRefreshTimer();
        const exp = this.getTokenExpiry();
        if (exp === null) return;
        const delay = exp * 1000 - Date.now() - 60_000;
        if (delay <= 0) {
            this.performTokenRefresh();
            return;
        }
        console.log(`Scheduling token refresh in ${Math.round(delay / 1000)} seconds`);
        this.refreshTokenTimeout = setTimeout(() => this.performTokenRefresh(), delay);
    }

    private performTokenRefresh() {
        // If the token was recently refreshed (e.g., by the interceptor handling a 401),
        // skip refreshing and re-schedule based on the new token's expiry instead.
        const exp = this.getTokenExpiry();
        if (exp !== null && exp * 1000 - Date.now() > 2 * 60 * 1000) {
            this.scheduleTokenRefresh();
            return;
        }

        console.log('Token refresh needed, time: ', new Date().toLocaleTimeString());
        const refreshToken = this.authenticationService.getRefreshToken();
        if (!refreshToken) {
            this.handleAutoLogout();
            return;
        }
        this.authenticationService
            .renewToken(refreshToken)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (response) => {
                    console.log('Token refreshed successfully, time: ', new Date().toLocaleTimeString());
                    this.authenticationService.storeTokens(response as LoginResponse);
                    this.scheduleTokenRefresh();
                },
                error: () => this.handleAutoLogout()
            });
    }

    private handleAutoLogout() {
        this.authenticationService.logout();
        this.injector.runInContext(() => {
            RoutesHelper.RunInContext.navigateToLoginAndTriggerLogout();
        });
    }
}
